import { CMS_TOKEN_URL } from 'app/config/env';
import { normalizeBearerToken } from 'shared/api/cms-access-token';
let cachedAccessToken = '';
let tokenExpiresAtMs = 0;
let inFlight = null;
let refreshTimer = null;
const TOKEN_REFRESH_SKEW_MS = 30000;
const MIN_REFRESH_DELAY_MS = 5000;
const isTokenValid = () => cachedAccessToken.length > 0 && Date.now() < tokenExpiresAtMs - TOKEN_REFRESH_SKEW_MS;
export const getCachedCmsAccessToken = () => isTokenValid() ? cachedAccessToken : null;
const scheduleTokenRefresh = (onRefresh, expiresInSec) => {
    if (refreshTimer) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
    }
    const delayMs = Math.max(expiresInSec * 1000 - TOKEN_REFRESH_SKEW_MS, MIN_REFRESH_DELAY_MS);
    refreshTimer = setTimeout(() => {
        refreshTimer = null;
        onRefresh();
    }, delayMs);
};
export const stopCmsTokenAutoRefresh = () => {
    if (refreshTimer) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
    }
};
export async function fetchCmsAccessToken() {
    const result = await fetchCmsAccessTokenWithMeta();
    return result.token;
}
export async function fetchCmsAccessTokenWithMeta() {
    if (isTokenValid()) {
        const remainingSec = Math.max(Math.floor((tokenExpiresAtMs - Date.now()) / 1000), 60);
        return { token: cachedAccessToken, expiresInSec: remainingSec };
    }
    if (inFlight) {
        return inFlight;
    }
    inFlight = (async () => {
        const res = await fetch(CMS_TOKEN_URL, {
            method: 'POST',
            headers: { Accept: 'application/json' },
        });
        if (!res.ok) {
            const detail = await res.text().catch(() => '');
            throw new Error(`CMS token request failed (${res.status})${detail ? `: ${detail.slice(0, 240)}` : ''}`);
        }
        const data = (await res.json());
        if (data.code !== 1) {
            throw new Error(data.message?.trim() || 'CMS token request returned an error.');
        }
        const token = normalizeBearerToken(data.object?.accessToken);
        if (!token) {
            throw new Error('CMS token response did not include object.accessToken.');
        }
        const expiresInSec = typeof data.object?.expiresIn === 'number' && data.object.expiresIn > 0
            ? data.object.expiresIn
            : 300;
        cachedAccessToken = token;
        tokenExpiresAtMs = Date.now() + expiresInSec * 1000;
        return { token, expiresInSec };
    })();
    try {
        return await inFlight;
    }
    finally {
        inFlight = null;
    }
}
/** Fetch CMS token once, then refresh automatically before expiry. */
export function startCmsTokenAutoRefresh(onToken) {
    let cancelled = false;
    const load = () => {
        void fetchCmsAccessTokenWithMeta()
            .then(result => {
            if (cancelled) {
                return;
            }
            onToken(result.token);
            scheduleTokenRefresh(load, result.expiresInSec);
        })
            .catch(() => {
            if (cancelled) {
                return;
            }
            scheduleTokenRefresh(load, 60);
        });
    };
    load();
    return () => {
        cancelled = true;
        stopCmsTokenAutoRefresh();
    };
}
export const clearCachedCmsAccessToken = () => {
    cachedAccessToken = '';
    tokenExpiresAtMs = 0;
    stopCmsTokenAutoRefresh();
};
